import * as jsonMapper from "json-mapper";
import {JSONMapped, jsonProperty} from "json-mapper-reflect";
import * as module from "module";
import * as moment from "moment";
import {Device} from "./device";
import {SubscriptionType} from "./subscription-type";

let expirationOffset = module.config().expirationOffset;
let pastExpirationCutoff = 60;

export class Subscription extends JSONMapped() {
	constructor(data: Partial<Subscription>) {
		super();
		Object.assign(this, data);
	}

	@jsonProperty()
	id: string;

	@jsonProperty()
	subscriptionTypeID: string;

	@jsonProperty()
	description: string;

	@jsonProperty(jsonMapper.localTimestampFromISO)
	effectiveAt: moment.Moment;

	@jsonProperty(jsonMapper.localTimestampFromISO)
	expiresAt: moment.Moment;

	@jsonProperty()
	maxDeviceCount: number;

	@jsonProperty()
	deviceIDs: Array<string>;

	@jsonProperty()
	autoRenew: boolean;


	// Set by service
	subscriptionType?: SubscriptionType;
	devices?: Array<Device>;


	get isAllocated() {
		return this.expiresAt != null;
	}

	get isDemo() {
		return false;
	}

	get isExpired() {
		return this.isAllocated && !this.expiresAt.isAfter();
	}

	get isRelevant() {
		// All current or unstarted subscriptions should be displayed
		if (!this.isExpired)
			return true;

		// If the subscription has expired but it doesn't have any devices, then it's probably
		// something the customer already renewed.
		if (this.deviceIDs.length === 0)
			return false;

		// If the subscripion expired more than 60 days ago, they probably don't want it anymore.
		let cuttoffDate = moment().subtract(pastExpirationCutoff, "days");
		return this.expiresAt.isAfter(cuttoffDate);
	}

	get renewalDate() {
		return moment(this.expiresAt).add(expirationOffset);
	}

	get defaultExpiration() {
		return moment().add(this.subscriptionType.renewalProduct.duration);
	}

	isCompatibleWith(subscription: Subscription) {
		let compatibility = COMPATIBLE_SUBSCRIPTION_TYPES[this.subscriptionTypeID];
		if (compatibility && compatibility.includes(subscription.subscriptionTypeID)) {
			let sharesDevices = this.deviceIDs.length === subscription.deviceIDs.length;

			if (sharesDevices) {
				this.deviceIDs.forEach(deviceID => {
					if (subscription.deviceIDs.indexOf(deviceID) === -1) sharesDevices = false;
				});
			}

			let expirationsDifferBy = moment.duration(Math.abs(subscription.expiresAt.diff(this.expiresAt)));
			return expirationsDifferBy.asDays() <= 60 && sharesDevices;
		}
		else {
			return false;
		}
	}

	expirationAfterRenewal(duration: moment.MomentInput) {
		if (this.expiresAt == null)
			return null;

		if (duration == null)
			duration = this.subscriptionType.renewalProduct.duration;

		// If the subscription is expired, the renewal should start at the current date.
		let start = moment.max(this.expiresAt, moment());

		return start
			.clone()
			.add(duration);
	}
}


// TODO: Don't hardcode this
const COMPATIBLE_SUBSCRIPTION_TYPES: Record<string, Array<string>> = {
	pilot_intl: ["eu_st", "eu_fc"],
	eu_st: ["pilot_intl", "eu_fc"],
	eu_fc: ["pilot_intl", "eu_st"],
	pilot_us: ["us_vfr", "us_ifr"],
	us_vfr: ["pilot_us", "us_ifr"],
	us_ifr: ["pilot_us", "us_vfr"],
};
